"use client";

import { motion, useTransform } from "motion/react";
import { chartCssVars } from "../chart-context";
import { useChartProjection } from "./chart-projection-context";
import {
  polygonPath,
  projectView,
  segmentsPath,
  type ViewTransform,
} from "./iso-projection";
import { useProjectedValue, useProjectionProgress } from "./use-projected-path";

export interface ProjectedFloorProps {
  xTicks?: ReadonlyArray<number>;
  yTicks?: ReadonlyArray<number>;
  zTicks?: ReadonlyArray<number>;
  walls?: boolean;
  floorFill?: string;
  wallFill?: string;
  stroke?: string;
  className?: string;
}

type Segment = readonly [readonly [number, number], readonly [number, number]];

interface FloorPaths {
  floor: string;
  walls: string;
  grid: string;
}

const EMPTY: FloorPaths = { floor: "", walls: "", grid: "" };

export function ProjectedFloor({
  xTicks = [],
  yTicks = [],
  zTicks = [],
  walls = true,
  floorFill = `color-mix(in oklab, ${chartCssVars.grid} 35%, transparent)`,
  wallFill = `color-mix(in oklab, ${chartCssVars.grid} 18%, transparent)`,
  stroke = chartCssVars.grid,
  className,
}: ProjectedFloorProps) {
  const projection = useChartProjection();
  const e = useProjectionProgress(projection);
  const opacity = useTransform(e, [0, 1], [0, 1]);
  const frame = projection?.frame;

  const paths = useProjectedValue(
    projection,
    (view: ViewTransform): FloorPaths => {
      if (!frame) return EMPTY;
      const { width: w, depth: d, zExtent: h } = frame;
      const p = (x: number, y: number, z: number) => projectView(view, x, y, z);
      const segments: Segment[] = [];
      for (const x of xTicks) segments.push([p(x, 0, 0), p(x, d, 0)]);
      for (const y of yTicks) segments.push([p(0, y, 0), p(w, y, 0)]);
      if (walls) {
        for (const z of zTicks) {
          segments.push([p(0, d, z), p(0, 0, z)]);
          segments.push([p(0, 0, z), p(w, 0, z)]);
        }
      }
      return {
        floor: polygonPath([p(0, 0, 0), p(w, 0, 0), p(w, d, 0), p(0, d, 0)]),
        walls: walls
          ? polygonPath([p(0, 0, 0), p(w, 0, 0), p(w, 0, h), p(0, 0, h)]) +
            polygonPath([p(0, 0, 0), p(0, d, 0), p(0, d, h), p(0, 0, h)])
          : "",
        grid: segmentsPath(segments),
      };
    },
    EMPTY
  );
  const floor = useTransform(paths, (v) => v.floor);
  const wallPath = useTransform(paths, (v) => v.walls);
  const grid = useTransform(paths, (v) => v.grid);

  if (!projection) {
    return null;
  }

  return (
    <motion.g
      aria-hidden
      className={className}
      pointerEvents="none"
      style={{ opacity }}
    >
      {walls && <motion.path d={wallPath} fill={wallFill} stroke="none" />}
      <motion.path d={floor} fill={floorFill} stroke="none" />
      <motion.path
        d={grid}
        fill="none"
        stroke={stroke}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
    </motion.g>
  );
}
